import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  ButtonGroup,
  InputAdornment,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  MenuItem,
  Popover,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import HistoryIcon from "@mui/icons-material/History";
import SearchIcon from "@mui/icons-material/Search";
import { historyIconCircleSx } from "./TaskActivityHistoryDrawer";
import { useSubscriptionGuard } from "../../hooks/useSubscriptionGuard";

/** Toolbar wrapped as a single elevated, rounded "pill" card. */
const actionsBarSx = {
  display: "flex",
  flexDirection: { xs: "column", lg: "row" },
  alignItems: { xs: "stretch", lg: "center" },
  justifyContent: "space-between",
  gap: { xs: 1, lg: 1.25 },
  mb: 0.5,
  px: { xs: 2, sm: 2.5 },
  py: { xs: 1.25, sm: 1.25 },
  bgcolor: "background.paper",
  border: (theme) => `1px solid ${theme.palette.divider}`,
  borderRadius: 2,
  boxShadow: (theme) =>
    theme.palette.mode === "dark" ? "none" : "0 1px 4px rgba(0,0,0,0.06)",
  flexShrink: 0,
  overflow: "visible",
};

const leftControlsSx = {
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  flexWrap: { xs: "wrap", lg: "nowrap" },
  gap: { xs: 1.25, sm: 1.5, md: 1.25, lg: 1.15, xl: 1.2 },
  flex: { xs: 1, lg: "1 1 auto" },
  minWidth: 0,
};

/** Badges, history icon, and create — same gap as between status chips (0.75). */
const toolbarTrailingSx = {
  display: "flex",
  alignItems: "center",
  flexWrap: { xs: "wrap", lg: "nowrap" },
  gap: { xs: 0.75, md: 0.6, lg: 0.7, xl: 0.75 },
  flexShrink: 0,
  ml: { xs: 0, lg: "auto" },
  overflow: "visible",
};

const filterFieldSx = {
  "& .MuiOutlinedInput-root": {
    overflow: "visible",
    minHeight: { lg: 38, xl: 38 },
  },
  "& .MuiInputBase-input": {
    fontSize: { xs: "0.92rem", sm: "0.98rem", md: "1rem", lg: "0.9rem", xl: "0.92rem" },
  },
};

const searchFieldSx = {
  ...filterFieldSx,
  flex: {
    xs: "1 1 300px",
    sm: "1 1 400px",
    md: "1 1 300px",
    lg: "0 1 280px",
    xl: "0 1 320px",
  },
  minWidth: { xs: 300, sm: 400, md: 260, lg: 260, xl: 300 },
  maxWidth: { xs: "100%", sm: 520, md: 340, lg: 340, xl: 380 },
  width: { lg: 280, xl: 320 },
  "& .MuiOutlinedInput-root": {
    overflow: "visible",
    minHeight: { xs: 40, sm: 42, md: 36, lg: 38, xl: 38 },
  },
  "& .MuiInputBase-input": {
    fontSize: { xs: "0.92rem", sm: "0.98rem", md: "0.88rem", lg: "0.92rem", xl: "0.94rem" },
    py: { md: 0.55, lg: 0.55, xl: 0.6 },
  },
};

const createButtonSx = {
  textTransform: "none",
  fontWeight: 600,
  fontSize: { xs: "0.95rem", sm: "1rem", md: "0.88rem", lg: "0.9rem", xl: "0.92rem" },
  px: { xs: 2, sm: 2.5, md: 1.75, lg: 1.85, xl: 2 },
  whiteSpace: "nowrap",
  py: { xs: 0.75, sm: 0.85, md: 0.45, lg: 0.5, xl: 0.52 },
  minHeight: { xs: 40, sm: 42, md: 34, lg: 36, xl: 38 },
};

const createButtonIconSx = {
  fontSize: { xs: "1.15rem", sm: "1.2rem", md: "1rem", lg: "1.02rem", xl: "1.05rem" },
};

const createMenuPaperSx = (theme, { showTemplatePanel }) => ({
  display: "flex",
  flexDirection: "row",
  alignItems: "stretch",
  overflow: "hidden",
  mt: 0.5,
  borderRadius: 1.5,
  boxShadow: theme.shadows[8],
  bgcolor: "background.paper",
  border: "1px solid",
  borderColor: theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.14)" : theme.palette.divider,
  ...(showTemplatePanel
    ? { minWidth: { xs: 300, sm: 560 } }
    : { minWidth: 220 }),
});

const templatePanelSx = {
  display: "flex",
  flexDirection: "column",
  width: { xs: 280, sm: 300 },
  minWidth: { xs: 280, sm: 300 },
  borderLeft: 1,
  borderColor: "divider",
  flexShrink: 0,
};

const templateSearchFieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 1.5,
    bgcolor: (theme) =>
      theme.palette.mode === "dark" ? alpha(theme.palette.common.white, 0.06) : "#f9f9f9",
    "& fieldset": {
      borderColor: "divider",
    },
    "&:hover fieldset": {
      borderColor: "primary.main",
    },
    "&.Mui-focused fieldset": {
      borderColor: "primary.main",
    },
  },
};

const TasksActions = ({
  activeTab,
  searchValue,
  onSearchChange,
  teamValue,
  onTeamChange,
  teamOptions = [],
  onCreateClick,
  canEditTemplates = false,
  canEditTasks = false,
  taskTemplateRows = [],
  onTaskCreateNew,
  onTaskCreateFromTemplate,
  canSeeHistory = false,
  onOpenHistory,
}) => {
  const isTasksTab = activeTab === 0;
  const [anchorEl, setAnchorEl] = useState(null);
  const [templatePanelOpen, setTemplatePanelOpen] = useState(false);
  const [templateSearchQuery, setTemplateSearchQuery] = useState("");

  const { guard } = useSubscriptionGuard();

  const handleCreateNewTask = async (mode) => {
    if (!await guard(['tasksCreated'])) return;
    onTaskCreateNew?.(mode);
  };

  const handleCreateFromTemplate = async (t) => {
    if (!await guard(['tasksCreated'])) return;
    handleTaskMenuClose();
    onTaskCreateFromTemplate?.(t);
  };

  const handleCreateTemplate = async () => {
    if (!await guard(['taskTemplatesCreated'])) return;
    onCreateClick?.();
  };
  const menuOpen = Boolean(anchorEl);

  const filteredTaskTemplates = useMemo(() => {
    const q = templateSearchQuery.trim().toLowerCase();
    if (!q) return taskTemplateRows;
    return taskTemplateRows.filter((t) => (t.name || "").toLowerCase().includes(q));
  }, [taskTemplateRows, templateSearchQuery]);

  const handleTaskMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
    setTemplatePanelOpen(false);
    setTemplateSearchQuery("");
  };

  const handleTaskMenuClose = () => {
    setAnchorEl(null);
    setTemplatePanelOpen(false);
    setTemplateSearchQuery("");
  };

  const createTaskButtonGroup = isTasksTab && canEditTasks && (
    <>
      <ButtonGroup
        variant="contained"
        color="primary"
        sx={{
          borderRadius: 2,
          flexShrink: 0,
          boxShadow: (theme) =>
            theme.palette.mode === "dark" ? theme.shadows[4] : theme.shadows[2],
          "& .MuiButtonGroup-grouped:not(:first-of-type)": {
            borderLeftWidth: "1px",
            borderLeftStyle: "solid",
            borderLeftColor: (theme) => alpha(theme.palette.primary.contrastText, 0.45),
          },
          "& .MuiButton-root": {
            textTransform: "none",
            color: "primary.contrastText",
            fontWeight: 600,
            "&:hover": {
              color: "primary.contrastText",
              bgcolor: "primary.dark",
            },
          },
        }}
      >
        <Button
          startIcon={<AddIcon sx={createButtonIconSx} />}
          onClick={() => handleCreateNewTask()}
          sx={{
            ...createButtonSx,
            borderRadius: "8px 0 0 8px",
          }}
        >
          Create New Task
        </Button>
        <Button
          onClick={handleTaskMenuOpen}
          aria-label="open create menu"
          aria-expanded={menuOpen}
          sx={{
            ...createButtonSx,
            minWidth: { xs: 48, sm: 50, md: 52 },
            px: { xs: 1, sm: 1.1, md: 1.25 },
            borderRadius: "0 8px 8px 0",
          }}
        >
          <ArrowDropDownIcon sx={createButtonIconSx} />
        </Button>
      </ButtonGroup>

      <Popover
        open={menuOpen}
        anchorEl={anchorEl}
        onClose={handleTaskMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: (theme) => createMenuPaperSx(theme, { showTemplatePanel: templatePanelOpen }),
          },
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "row", alignItems: "stretch" }}>
          <List dense disablePadding sx={{ py: 0.5, minWidth: 200, flexShrink: 0 }}>
            <ListItemButton
              onClick={() => {
                handleTaskMenuClose();
                handleCreateNewTask("split");
              }}
              sx={{ py: 1.1, px: 2 }}
            >
              <ListItemText
                primary="New task"
                primaryTypographyProps={{ fontSize: "0.95rem", textTransform: "none" }}
              />
            </ListItemButton>

            {taskTemplateRows.length > 0 && (
              <ListItemButton
                selected={templatePanelOpen}
                onClick={() => setTemplatePanelOpen((v) => !v)}
                sx={{
                  py: 1.1,
                  px: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                }}
              >
                <ListItemText
                  primary="Task from template"
                  primaryTypographyProps={{ fontSize: "0.95rem", fontWeight: 600 }}
                />
                <ChevronRightIcon
                  fontSize="small"
                  sx={{ color: "text.secondary", flexShrink: 0 }}
                  aria-hidden
                />
              </ListItemButton>
            )}
          </List>

          {templatePanelOpen && taskTemplateRows.length > 0 && (
            <Box sx={templatePanelSx}>
              <Box sx={{ px: 1.5, pt: 1.5, pb: 1, flexShrink: 0 }}>
                <TextField
                  size="small"
                  fullWidth
                  autoFocus
                  placeholder="Search"
                  value={templateSearchQuery}
                  onChange={(e) => setTemplateSearchQuery(e.target.value)}
                  sx={templateSearchFieldSx}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" sx={{ color: "text.secondary" }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              {filteredTaskTemplates.length === 0 ? (
                <Box sx={{ px: 2, py: 2.5, textAlign: "center", flex: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    No templates match your search.
                  </Typography>
                </Box>
              ) : (
                <List
                  dense
                  disablePadding
                  sx={{
                    overflow: "auto",
                    flex: 1,
                    minHeight: 0,
                    maxHeight: { xs: 280, sm: 320 },
                    py: 0.25,
                  }}
                >
                  {filteredTaskTemplates.map((t) => (
                    <ListItemButton
                      key={t.id}
                      onClick={() => handleCreateFromTemplate(t)}
                      sx={{
                        py: 0.9,
                        px: 1.5,
                        mx: 0.75,
                        borderRadius: 1,
                        "&:hover": {
                          bgcolor: (theme) =>
                            theme.palette.mode === "dark"
                              ? alpha(theme.palette.primary.main, 0.14)
                              : alpha(theme.palette.primary.main, 0.08),
                        },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <Box
                          sx={{
                            width: 28,
                            height: 28,
                            borderRadius: "50%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            bgcolor: "primary.main",
                            color: "primary.contrastText",
                          }}
                        >
                          <DescriptionOutlinedIcon sx={{ fontSize: 16 }} />
                        </Box>
                      </ListItemIcon>
                      <ListItemText
                        primary={t.name}
                        primaryTypographyProps={{
                          fontSize: "0.92rem",
                          noWrap: true,
                        }}
                      />
                    </ListItemButton>
                  ))}
                </List>
              )}
            </Box>
          )}
        </Box>
      </Popover>
    </>
  );

  const createTemplateButton = !isTasksTab && canEditTemplates && (
    <Button
      variant="contained"
      color="primary"
      startIcon={<AddIcon sx={createButtonIconSx} />}
      onClick={handleCreateTemplate}
      sx={{
        ...createButtonSx,
        borderRadius: 2,
        flexShrink: 0,
      }}
    >
      Create Task Template
    </Button>
  );

  const showCreate = Boolean(createTaskButtonGroup || createTemplateButton);

  const historyButton = canSeeHistory && (
    <Tooltip title="Activity history (last 30 days)">
      <IconButton
        onClick={() => onOpenHistory?.()}
        aria-label="Open activity history"
        sx={{
          p: 0.5,
          ...historyIconCircleSx,
          width: { xs: 38, sm: 40, md: 32, lg: 36, xl: 38 },
          height: { xs: 38, sm: 40, md: 32, lg: 36, xl: 38 },
          "&:hover": {
            backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.18),
            borderColor: "primary.main",
          },
        }}
      >
        <HistoryIcon sx={{ fontSize: { xs: "1.2rem", sm: "1.3rem", md: "1.1rem", lg: "1.15rem", xl: "1.2rem" } }} />
      </IconButton>
    </Tooltip>
  );

  return (
    <Box sx={actionsBarSx}>
      <Box sx={leftControlsSx}>
        <TextField
          size="small"
          placeholder="Filter and search"
          value={searchValue}
          onChange={(event) => onSearchChange(event.target.value)}
          sx={searchFieldSx}
        />

        {isTasksTab && teamOptions.length > 0 && (
          <TextField
            select
            size="small"
            value={teamValue}
            onChange={(event) => onTeamChange(event.target.value)}
            SelectProps={{
              MenuProps: {
                anchorOrigin: { vertical: "bottom", horizontal: "left" },
                transformOrigin: { vertical: "top", horizontal: "left" },
                PaperProps: {
                  sx: {
                    mt: 0.5,
                    minWidth: 200,
                    maxHeight: 320,
                    borderRadius: 1.5,
                    boxShadow: 4,
                    "& .MuiList-root": { py: 0.5 },
                    "& .MuiMenuItem-root": {
                      fontSize: "0.9rem",
                      py: 0.85,
                      px: 1.5,
                      minHeight: "auto",
                      borderRadius: 1,
                      mx: 0.5,
                    },
                  },
                },
              },
            }}
            sx={{
              ...filterFieldSx,
              minWidth: { xs: 150, sm: 170, md: 160, lg: 165, xl: 170 },
              "& .MuiInputBase-input": {
                display: "flex",
                alignItems: "center",
                fontSize: { xs: "0.92rem", sm: "0.98rem", md: "1rem", lg: "0.9rem", xl: "0.92rem" },
              },
              // Active-filter indicator: brand-green border when a specific team is
              // selected (i.e. not the default "All Teams").
              ...(teamValue && teamValue !== "all"
                ? {
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: (theme) => theme.palette.primary.main,
                      borderWidth: "2.5px",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: (theme) => theme.palette.primary.main,
                      borderWidth: "2.5px",
                    },
                    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: (theme) => theme.palette.primary.main,
                      borderWidth: "2.5px",
                    },
                  }
                : {}),
            }}
          >
            {teamOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        )}
      </Box>

      {historyButton || showCreate ? (
        <Box sx={toolbarTrailingSx}>
          {historyButton}
          {createTaskButtonGroup}
          {createTemplateButton}
        </Box>
      ) : null}
    </Box>
  );
};

export default TasksActions;
